import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { SUBTASK_TYPES_WITH_LEVEL, SubtaskTypes } from "./projectConfigConstants";
import authService from "../../../utils/authService";
import { Phase, Subject, Topic, DeleteTarget, ContentItem, Subtask, SUBTASK_TYPE } from "./ProjectConfigurationTypes";
import PhaseComponent from "./PhaseComponent";
import PreviewModal from "./PreviewModal";
import ProjectConfigurationHeader from "./ProjectConfigurationHeader";

interface ContentTypeCounts {
  video: number;
  notes: number;
  mcq: number;
  coding: number;
  project_coding: number;
}

interface ContentTypeStatus {
  assigned: string[];
  uploaded: string[];
  approved: string[];
}

interface ContentStatusData {
  assigned: ContentTypeCounts;
  uploaded: ContentTypeCounts;
  approved: ContentTypeCounts;
  selected: ContentTypeCounts;
  total: ContentTypeCounts;
  video: ContentTypeStatus;
  notes: ContentTypeStatus;
  mcq: ContentTypeStatus;
  coding: ContentTypeStatus;
  project_coding: ContentTypeStatus;
}

const ProjectConfiguration: React.FC = () => {
  const location = useLocation();
  const navigationState = (location.state as any) || {};
  
  const [phases, setPhases] = useState<Phase[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [projectAllowedSubjects, setProjectAllowedSubjects] = useState<string[]>([]);
  const [topicsCache, setTopicsCache] = useState<Record<string, Topic[]>>({});
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedTrack, setSelectedTrack] = useState<string>(navigationState.track_id || "");
  const [selectedProject, setSelectedProject] = useState<string>(navigationState.project_id || "");
  const [selectedProjectName, setSelectedProjectName] = useState<string>(navigationState.project_name || "");
  const [assignedCcIds, setAssignedCcIds] = useState<string[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewContent, setPreviewContent] = useState<{ type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES; content: any; subtask: any } | null>(null);
  const [contentStatus, setContentStatus] = useState<{
    [SUBTASK_TYPE.VIDEO]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.NOTES]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.MCQ]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    [SUBTASK_TYPE.PROJECT_CODING]?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null>(null);
  const [contentStatusData, setContentStatusData] = useState<ContentStatusData | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [projectStatus, setProjectStatus] = useState<string>("");
  const [showPublishedEditModal, setShowPublishedEditModal] = useState<boolean>(false);
  const [pendingAutoSave, setPendingAutoSave] = useState<boolean>(false);
  const navigate = useNavigate();
  const refreshProjectsRef = useRef<(() => Promise<void>) | null>(null);
  const [lastUpdatableTag, setLastUpdatableTag] = useState<string>("");

  /**
   * Fetches content status for the project
   * @param projectId - The project ID to fetch status for
   */
  const fetchContentStatus = async (projectId: string) => {
    if (!projectId) {
      setContentStatus(null);
      setContentStatusData(null);
      return;
    }
    
    try {
      const response = await authService.get(`admin/content/project/content_status/${projectId}/`);
      if (response.data) {
        setContentStatus({
          [SUBTASK_TYPE.VIDEO]: response.data[SUBTASK_TYPE.VIDEO] || {},
          [SUBTASK_TYPE.NOTES]: response.data[SUBTASK_TYPE.NOTES] || {},
          [SUBTASK_TYPE.MCQ]: response.data[SUBTASK_TYPE.MCQ] || {},
          [SUBTASK_TYPE.CODING]: response.data[SUBTASK_TYPE.CODING] || {},
          [SUBTASK_TYPE.PROJECT_CODING]: response.data[SUBTASK_TYPE.PROJECT_CODING] || {}
        });
        // Store full content status data for header
        setContentStatusData(response.data);
      }
    } catch (error) {
      console.error('Error fetching content status:', error);
      setContentStatus(null);
      setContentStatusData(null);
    }
  };

  /**
   * Effect: Fetch content status when selectedProject changes (including initial load)
   * This ensures content status is fetched both when project is selected via dropdown
   * and when project is loaded from navigation state
   */
  useEffect(() => {
    if (selectedProject) {
      fetchContentStatus(selectedProject);
    } else {
      setContentStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  /**
   * Effect: Auto-save project configuration when phases change
   * Debounced to avoid excessive API calls (2 second delay)
   * Only saves if project is selected and phases exist
   */
  useEffect(() => {
    if (!selectedProject || phases.length === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      autoSave();
    }, 2000); // 2 second debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases, selectedProject]);

  /**
   * Effect: Auto-expands the first phase, part, task, and subtask when phases are loaded
   * Step 1: Check if phases exist and no items are currently expanded
   * Step 2: Add first phase ID to expanded set
   * Step 3: Add first part ID to expanded set if parts exist
   * Step 4: Add first task ID to expanded set if tasks exist
   * Step 5: Add first subtask ID to expanded set if subtasks exist
   * Step 6: Update expandedItems state
   */
  useEffect(() => {
    if (phases.length > 0 && expandedItems.size === 0) {
      const firstPhase = phases[0];
      if (firstPhase) {
        const newSet = new Set<string>();
        // Step 2: Add first phase
        newSet.add(firstPhase.id);
        
        // Step 3: Add first part
        if (firstPhase.parts.length > 0) {
          const firstPart = firstPhase.parts[0];
          const firstPartId = `${firstPhase.id}-${firstPart.id}`;
          newSet.add(firstPartId);
          
          // Step 4: Add first task
          if (firstPart.tasks.length > 0) {
            const firstTask = firstPart.tasks[0];
            const firstTaskId = `${firstPartId}-${firstTask.id}`;
            newSet.add(firstTaskId);
            
            // Step 5: Add first subtask
            if (firstTask.subtasks.length > 0) {
              const firstSubtask = firstTask.subtasks[0];
              const firstSubtaskId = `${firstTaskId}-${firstSubtask.subtask_id}`;
              newSet.add(firstSubtaskId);
            }
          }
        }
        
        // Step 6: Update state
        setExpandedItems(newSet);
      }
    }
  }, [phases.length]);

  /**
   * Updates the subjects state when subjects change
   * @param newSubjects - Array of subject objects to set
   */
  const handleSubjectsChange = (newSubjects: Subject[]) => {
    setSubjects(newSubjects);
  };

  /**
   * Handles project selection change and transforms project data into phases structure
   * Step 1: Update selected project state (projectId, projectName, allowedSubjects)
   * Step 2: Check if projectData contains content array
   * Step 3: Transform phases data (map phase_id/phase_name to id/name)
   * Step 4: Transform parts data (map part_id/part_name to id/name)
   * Step 5: Transform tasks data (map task_id/task_name to id/name)
   * Step 6: Transform subtasks data (handle select/assign modes, determine selectedContentLevel)
   * Step 7: Collect unique subject IDs from all subtasks
   * Step 8: Fetch topics for each unique subject ID
   * Step 9: Set transformed phases in state
   * @param projectId - The selected project ID
   * @param projectName - The selected project name
   * @param allowedSubjects - Optional array of allowed subject IDs for this project
   * @param projectData - Optional project data object containing content structure
   */

  const handleProjectChange = async (projectId: string, projectName: string, allowedSubjects?: string[], projectData?: any) => {
    // Step 1: Update project state
    setSelectedProject(projectId);
    setSelectedProjectName(projectName);
    setProjectAllowedSubjects(allowedSubjects || []);
    setAssignedCcIds(projectData?.assigned_cc_ids || null);
    
    // Fetch content status for the project
    await fetchContentStatus(projectId);
    
    // Step 2: Check if project data exists
    if (projectData?.content && Array.isArray(projectData.content) && projectData.content.length > 0) {
      // Step 3-6: Transform project data structure
      const transformedPhases = projectData.content.map((phase: any) => ({
        id: phase.phase_id || phase.id || "",
        name: phase.phase_name || phase.name || "",
        parts: (phase.parts || []).map((part: any) => ({
          id: part.part_id || part.id || "",
          name: part.part_name || part.name || "",
          tasks: (part.tasks || []).map((task: any) => ({
            id: task.task_id || task.id || "",
            name: task.task_name || task.name || "",
            subtasks: (task.data || task.subtasks || []).map((subtask: any) => {
              const isVideoOrNotes = subtask.type === SUBTASK_TYPE.VIDEO || subtask.type === SUBTASK_TYPE.NOTES;
              const isProjectCoding = subtask.type === SUBTASK_TYPE.PROJECT_CODING;
              const isMcqCoding = subtask.type === SUBTASK_TYPE.MCQ || subtask.type === SUBTASK_TYPE.CODING;
              const isAssignMode = subtask.mode === "assign";
              const isSelectMode = subtask.mode === "select";
              
              // Determine selectedContentLevel for mcq/coding
              let selectedContentLevel = "";
              let selectedContentId = "";
              
              if ((isVideoOrNotes || isProjectCoding) && (isSelectMode || isAssignMode)) {
                selectedContentLevel = subtask.level || "";
                selectedContentId = subtask.id || "";
              } else if (isMcqCoding) {
                // Auto-select level1 if it has data, otherwise check level2, level3
                if (Array.isArray(subtask.level1) && subtask.level1.length > 0) {
                  selectedContentLevel = "level1";
                  selectedContentId = subtask.level1.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else if (Array.isArray(subtask.level2) && subtask.level2.length > 0) {
                  selectedContentLevel = "level2";
                  selectedContentId = subtask.level2.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else if (Array.isArray(subtask.level3) && subtask.level3.length > 0) {
                  selectedContentLevel = "level3";
                  selectedContentId = subtask.level3.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else {
                  selectedContentLevel = subtask.selectedContentLevel || "";
                  selectedContentId = subtask.selectedContentId || "";
                }
              } else {
                selectedContentLevel = subtask.selectedContentLevel || "";
                selectedContentId = subtask.selectedContentId || "";
              }
              
              const transformedSubtask: any = {
                subtask_id: subtask.subtask_id || "", // Subtask identifier from JSON
                id: (isVideoOrNotes || isProjectCoding) && (isSelectMode || isAssignMode) ? (subtask.id || "") : undefined, // Question/content ID from JSON (only for VIDEO/NOTES/PROJECT_CODING)
                name: subtask.subtask_name || subtask.name || "",
                isMandatory: subtask.is_mandatory ?? subtask.isMandatory ?? true,
                showDropdowns: !!(subtask.mode || subtask.actionType || subtask.subject_id || subtask.subjectId),
                actionType: subtask.mode || subtask.actionType,
                subjectId: subtask.subject_id || subtask.subjectId || "",
                topicId: subtask.topic_id || subtask.topicId || "",
                subtopicId: subtask.sub_topic_id || subtask.subtopicId || "",
                subtaskType: (subtask.type || subtask.subtaskType || "") as SubtaskTypes,
                level: subtask.level || "",
                selectedContentId: selectedContentId,
                selectedContentTime: isVideoOrNotes && isSelectMode ? subtask.time : (subtask.selectedContentTime || ""),
                selectedContentLevel: selectedContentLevel,
                createdContent: subtask.createdContent,
                availableContent: subtask.availableContent,
                // Preserve level arrays for mcq/coding types
                level1: subtask.level1 || [],
                level2: subtask.level2 || [],
                level3: subtask.level3 || [],
                // Preserve editor for coding type
                editor: subtask.editor || null,
                // Preserve description for project_coding
                description: isProjectCoding ? (subtask.description || "") : undefined,
                // Preserve cc_id from user_id for PROJECT_CODING in assign mode
                cc_id: isProjectCoding && isAssignMode && subtask.user_id ? subtask.user_id : undefined
              };
              return transformedSubtask as Subtask;
            })
          }))
        }))
      }));
      setPhases(transformedPhases);
      
      const subjectsToFetch = new Set<string>();
      transformedPhases.forEach((phase: Phase) => {
        phase.parts.forEach((part: any) => {
          part.tasks.forEach((task: any) => {
            task.subtasks.forEach((subtask: any) => {
              if (subtask.subjectId) {
                subjectsToFetch.add(subtask.subjectId);
              }
            });
          });
        });
      });
      
      subjectsToFetch.forEach((subjectId: string) => {
        fetchTopics(subjectId);
      });
    } else {
      setPhases([]);
    }
  };

  const filteredSubjects = projectAllowedSubjects.length > 0
    ? subjects.filter(subject => projectAllowedSubjects.includes(subject.subject_id))
    : subjects;

  /**
   * Fetches topics and subtopics for a given subject with caching
   * Step 1: Validate subjectId exists
   * Step 2: Check if topics are already in memory cache
   * Step 3: Check sessionStorage for cached topics
   * Step 4: If cached data exists, parse and use it
   * Step 5: If not cached, fetch from API
   * Step 6: Store fetched topics in both memory cache and sessionStorage
   * @param subjectId - The subject ID to fetch topics for
   */
  const fetchTopics = async (subjectId: string) => {
    // Step 1: Validation
    if (!subjectId) return;
    // Step 2: Check memory cache
    if (topicsCache[subjectId]) return;
    
    // Step 3: Check sessionStorage cache
    const cacheKey = `topics_subtopics_${subjectId}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    
    // Step 4: Use cached data if available
    if (cachedData) {
      try {
        const topics = JSON.parse(cachedData);
        setTopicsCache(prev => ({ ...prev, [subjectId]: topics }));
        return;
      } catch (e) {
        console.error('Error parsing cached topics:', e);
      }
    }
    
    // Step 5: Fetch from API
    try {
      const response = await authService.post(`content_creation/topics_subtopics_by_subject/`, {
        subject_id: subjectId
      });
      const topics = response.data.topics || [];
      // Step 6: Store in both caches
      setTopicsCache(prev => ({ ...prev, [subjectId]: topics }));
      sessionStorage.setItem(cacheKey, JSON.stringify(topics));
    } catch (error) {
      console.error('Error fetching topics:', error);
    }
  };

  /**
   * Parses the last_updatable_tag to extract phase, part, task, and subtask numbers
   * Format: "phase2#part1#task1#subtask1"
   * @param tag - The last_updatable_tag string
   * @returns Object with parsed numbers or null if tag is empty/invalid
   */
  const parseUpdatableTag = (tag: string): { phase: number; part: number; task: number; subtask: number } | null => {
    if (!tag || tag.trim() === "") return null;
    
    const parts = tag.split('#');
    if (parts.length !== 4) return null;
    
    try {
      const phaseNum = parseInt(parts[0].replace('phase', ''), 10);
      const partNum = parseInt(parts[1].replace('part', ''), 10);
      const taskNum = parseInt(parts[2].replace('task', ''), 10);
      const subtaskNum = parseInt(parts[3].replace('subtask', ''), 10);
      
      if (isNaN(phaseNum) || isNaN(partNum) || isNaN(taskNum) || isNaN(subtaskNum)) {
        return null;
      }
      
      return { phase: phaseNum, part: partNum, task: taskNum, subtask: subtaskNum };
    } catch (error) {
      return null;
    }
  };

  /**
   * Extracts number from ID string (e.g., "phase2" -> 2, "part1" -> 1)
   * @param id - The ID string
   * @param prefix - The prefix to remove ("phase", "part", "task", "subtask")
   * @returns The number or 0 if invalid
   */
  const extractNumberFromId = (id: string, prefix: string): number => {
    const num = parseInt(id.replace(prefix, ''), 10);
    return isNaN(num) ? 0 : num;
  };

  /**
   * Checks if a phase is restricted based on last_updatable_tag
   * The tag marks the LAST updatable item (inclusive), so items at or before the tag are restricted
   * @param phaseId - The phase ID (e.g., "phase1", "phase2")
   * @returns true if restricted, false if allowed
   */
  const isPhaseRestricted = (phaseId: string): boolean => {
    if (!lastUpdatableTag) return false;
    const tagData = parseUpdatableTag(lastUpdatableTag);
    if (!tagData) return false;
    
    const phaseNum = extractNumberFromId(phaseId, 'phase');
    // Restrict if phase is at or before the tag position
    return phaseNum <= tagData.phase;
  };

  /**
   * Checks if a part is restricted based on last_updatable_tag
   * The tag marks the LAST updatable item (inclusive), so items at or before the tag are restricted
   * @param phaseId - The phase ID
   * @param partId - The part ID
   * @returns true if restricted, false if allowed
   */
  const isPartRestricted = (phaseId: string, partId: string): boolean => {
    if (!lastUpdatableTag) return false;
    const tagData = parseUpdatableTag(lastUpdatableTag);
    if (!tagData) return false;
    
    const phaseNum = extractNumberFromId(phaseId, 'phase');
    const partNum = extractNumberFromId(partId, 'part');
    
    // Restrict if phase is before the tag phase
    if (phaseNum < tagData.phase) return true;
    // Restrict if same phase and part is at or before the tag part
    if (phaseNum === tagData.phase && partNum <= tagData.part) return true;
    return false;
  };

  /**
   * Checks if a task is restricted based on last_updatable_tag
   * The tag marks the LAST updatable item (inclusive), so items at or before the tag are restricted
   * @param phaseId - The phase ID
   * @param partId - The part ID
   * @param taskId - The task ID
   * @returns true if restricted, false if allowed
   */
  const isTaskRestricted = (phaseId: string, partId: string, taskId: string): boolean => {
    if (!lastUpdatableTag) return false;
    const tagData = parseUpdatableTag(lastUpdatableTag);
    if (!tagData) return false;
    
    const phaseNum = extractNumberFromId(phaseId, 'phase');
    const partNum = extractNumberFromId(partId, 'part');
    const taskNum = extractNumberFromId(taskId, 'task');
    
    // Restrict if phase is before the tag phase
    if (phaseNum < tagData.phase) return true;
    // Restrict if same phase and part is before the tag part
    if (phaseNum === tagData.phase && partNum < tagData.part) return true;
    // Restrict if same phase/part and task is at or before the tag task
    if (phaseNum === tagData.phase && partNum === tagData.part && taskNum <= tagData.task) return true;
    return false;
  };

  /**
   * Checks if a subtask is restricted based on last_updatable_tag
   * The tag marks the LAST updatable item (inclusive), so items at or before the tag are restricted
   * @param phaseId - The phase ID
   * @param partId - The part ID
   * @param taskId - The task ID
   * @param subtaskId - The subtask ID
   * @returns true if restricted, false if allowed
   */
  const isSubtaskRestricted = (phaseId: string, partId: string, taskId: string, subtaskId: string): boolean => {
    if (!lastUpdatableTag) return false;
    const tagData = parseUpdatableTag(lastUpdatableTag);
    if (!tagData) return false;
    
    const phaseNum = extractNumberFromId(phaseId, 'phase');
    const partNum = extractNumberFromId(partId, 'part');
    const taskNum = extractNumberFromId(taskId, 'task');
    const subtaskNum = extractNumberFromId(subtaskId, 'subtask');
    
    // Restrict if phase is before the tag phase
    if (phaseNum < tagData.phase) return true;
    // Restrict if same phase and part is before the tag part
    if (phaseNum === tagData.phase && partNum < tagData.part) return true;
    // Restrict if same phase/part and task is before the tag task
    if (phaseNum === tagData.phase && partNum === tagData.part && taskNum < tagData.task) return true;
    // Restrict if same phase/part/task and subtask is at or before the tag subtask
    if (phaseNum === tagData.phase && partNum === tagData.part && taskNum === tagData.task && subtaskNum <= tagData.subtask) return true;
    return false;
  };

  /**
   * Generates a sequential ID for phases, parts, tasks, or subtasks
   * @param prefix - The prefix for the ID (e.g., "phase", "part", "task", "subtask")
   * @param index - The zero-based index to convert to 1-based ID
   * @returns Formatted ID string (e.g., "phase1", "part2")
   */
  const generateId = (prefix: string, index: number) => `${prefix}${index + 1}`;

  /**
   * Expands an item and ensures parent items are also expanded, collapsing siblings at the same level
   * Step 1: Parse the ID to determine hierarchy level (phase, part, task, or subtask)
   * Step 2: For phase (1 part): Collapse other phases, add this phase
   * Step 3: For part (2 parts): Ensure parent phase is expanded, collapse sibling parts, add this part
   * Step 4: For task (3 parts): Ensure parent phase and part are expanded, collapse sibling tasks, add this task
   * Step 5: For subtask (4 parts): Ensure all parents are expanded, collapse sibling subtasks, add this subtask
   * @param id - The hierarchical ID (e.g., "phase1", "phase1-part1", "phase1-part1-task1", "phase1-part1-task1-subtask1")
   */
  const expandItem = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      const parts = id.split('-');
      
      // Step 2: Handle phase level (1 part)
      if (parts.length === 1) {
        // Collapse other phases
        newSet.forEach(item => {
          if (item.split('-').length === 1) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 3: Handle part level (2 parts)
      else if (parts.length === 2) {
        const parentId = parts[0];
        // Ensure parent phase is expanded
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling parts
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 2 && itemParts[0] === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 4: Handle task level (3 parts)
      else if (parts.length === 3) {
        const parentId = `${parts[0]}-${parts[1]}`;
        // Ensure parent phase and part are expanded
        if (!newSet.has(parts[0])) {
          newSet.add(parts[0]);
        }
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling tasks
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 3 && `${itemParts[0]}-${itemParts[1]}` === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 5: Handle subtask level (4 parts)
      else if (parts.length === 4) {
        const parentId = `${parts[0]}-${parts[1]}-${parts[2]}`;
        // Ensure all parents are expanded
        if (!newSet.has(parts[0])) {
          newSet.add(parts[0]);
        }
        if (!newSet.has(`${parts[0]}-${parts[1]}`)) {
          newSet.add(`${parts[0]}-${parts[1]}`);
        }
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling subtasks
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 4 && `${itemParts[0]}-${itemParts[1]}-${itemParts[2]}` === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      }
      
      return newSet;
    });
  };

  /**
   * Adds a new phase to the project configuration
   * Step 1: Generate new phase ID based on current phases length
   * Step 2: Create new phase object with empty name and parts array
   * Step 3: Add new phase to phases array
   * Step 4: Auto-expand the new phase
   */
  const addPhase = () => {
    // Step 1 & 2: Generate ID and create phase
    const newPhase: Phase = {
      id: generateId("phase", phases.length),
      name: "",
      parts: []
    };
    // Step 3: Add to phases array
    setPhases([...phases, newPhase]);
    // Step 4: Expand the new phase
    expandItem(newPhase.id);
  };

  /**
   * Adds a new part to a specific phase
   * Step 1: Get a copy of the phases array
   * Step 2: Get the target phase
   * Step 3: Check if phase is restricted
   * Step 4: Generate new part ID based on current parts length
   * Step 5: Create new part object with empty name and tasks array
   * Step 6: Add new part to phase's parts array
   * Step 7: Update phases state
   * Step 8: Auto-expand the new part
   * @param phaseIndex - The index of the phase to add the part to
   */
  const addPart = (phaseIndex: number) => {
    const phase = phases[phaseIndex];
    // Step 1 & 2: Get phases copy and target phase
    const updatedPhases = [...phases];
    // Step 3 & 4: Generate ID and create part
    const newPart = {
      id: generateId("part", phase.parts.length),
      name: "",
      tasks: []
    };
    // Step 5: Add part to phase
    updatedPhases[phaseIndex].parts.push(newPart);
    // Step 6: Update state
    setPhases(updatedPhases);
    // Step 7: Expand the new part
    expandItem(`${phase.id}-${newPart.id}`);
  };

  /**
   * Adds a new task to a specific part within a phase
   * Step 1: Get a copy of the phases array
   * Step 2: Get the target part
   * Step 3: Check if part is restricted
   * Step 4: Generate new task ID based on current tasks length
   * Step 5: Create new task object with empty name and subtasks array
   * Step 6: Add new task to part's tasks array
   * Step 7: Update phases state
   * Step 8: Auto-expand the new task
   * @param phaseIndex - The index of the phase
   * @param partIndex - The index of the part to add the task to
   */
  const addTask = (phaseIndex: number, partIndex: number) => {
    const phase = phases[phaseIndex];
    const part = phase.parts[partIndex];
    if (isPartRestricted(phase.id, part.id)) {
      return; // Restricted - cannot add task
    }
    // Step 1 & 2: Get phases copy and target part
    const updatedPhases = [...phases];
    // Step 3 & 4: Generate ID and create task
    const newTask = {
      id: generateId("task", part.tasks.length),
      name: "",
      subtasks: []
    };
    // Step 5: Add task to part
    updatedPhases[phaseIndex].parts[partIndex].tasks.push(newTask);
    // Step 6: Update state
    setPhases(updatedPhases);
    // Step 7: Expand the new task
    expandItem(`${phase.id}-${part.id}-${newTask.id}`);
  };

  /**
   * Adds a new subtask to a specific task within a part and phase
   * Step 1: Get a copy of the phases array
   * Step 2: Get the target task
   * Step 3: Check if task is restricted
   * Step 4: Generate new subtask ID based on current subtasks length
   * Step 5: Create new subtask object with default values
   * Step 6: Add new subtask to task's subtasks array
   * Step 7: Update phases state
   * Step 8: Auto-expand the new subtask
   * @param phaseIndex - The index of the phase
   * @param partIndex - The index of the part
   * @param taskIndex - The index of the task to add the subtask to
   */
  const addSubtask = (phaseIndex: number, partIndex: number, taskIndex: number) => {
    const phase = phases[phaseIndex];
    const part = phase.parts[partIndex];
    const task = part.tasks[taskIndex];
    if (isTaskRestricted(phase.id, part.id, task.id)) {
      return; // Restricted - cannot add subtask
    }
    // Step 1 & 2: Get phases copy and target task
    const updatedPhases = [...phases];
    // Step 3 & 4: Generate ID and create subtask with defaults
    const newSubtask: Subtask = {
      subtask_id: generateId("subtask", task.subtasks.length),
      name: "",
      isMandatory: true,
      showDropdowns: false,
      subjectId: "",
      topicId: "",
      subtopicId: "",
      subtaskType: "" as SubtaskTypes,
      level: ""
    };
    // Step 5: Add subtask to task
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks.push(newSubtask);
    // Step 6: Update state
    setPhases(updatedPhases);
    // Step 7: Expand the new subtask
    expandItem(`${phase.id}-${part.id}-${task.id}-${newSubtask.subtask_id}`);
  };

  /**
   * Updates the name of a phase
   * @param phaseIndex - The index of the phase to update
   * @param name - The new name for the phase
   */
  const updatePhaseName = (phaseIndex: number, name: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].name = name;
    setPhases(updatedPhases);
  };

  /**
   * Updates the name of a part
   * @param phaseIndex - The index of the phase containing the part
   * @param partIndex - The index of the part to update
   * @param name - The new name for the part
   */
  const updatePartName = (phaseIndex: number, partIndex: number, name: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].name = name;
    setPhases(updatedPhases);
  };

  /**
   * Updates the name of a task
   * @param phaseIndex - The index of the phase
   * @param partIndex - The index of the part containing the task
   * @param taskIndex - The index of the task to update
   * @param name - The new name for the task
   */
  const updateTaskName = (phaseIndex: number, partIndex: number, taskIndex: number, name: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].name = name;
    setPhases(updatedPhases);
  };

  /**
   * Updates the name of a subtask
   * @param phaseIndex - The index of the phase
   * @param partIndex - The index of the part
   * @param taskIndex - The index of the task containing the subtask
   * @param subtaskIndex - The index of the subtask to update
   * @param name - The new name for the subtask
   */
  const updateSubtaskName = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, name: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].name = name;
    setPhases(updatedPhases);
  };

  /**
   * Toggles the expand/collapse state of an item, collapsing siblings at the same level
   * Step 1: Check if item is currently expanded - if yes, collapse it
   * Step 2: If not expanded, parse ID to determine level
   * Step 3: Collapse siblings at the same level (phases, parts, tasks, or subtasks)
   * Step 4: Expand the selected item
   * @param id - The hierarchical ID of the item to toggle
   */
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      
      // Step 1: Toggle if already expanded
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        // Step 2: Parse ID to determine level
        const parts = id.split('-');
        
        // Step 3 & 4: Handle different hierarchy levels
        if (parts.length === 1) {
          // Phase level: collapse other phases
          newSet.forEach(item => {
            if (item.split('-').length === 1) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 2) {
          // Part level: collapse sibling parts
          const parentId = parts[0];
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 2 && itemParts[0] === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 3) {
          // Task level: collapse sibling tasks
          const parentId = `${parts[0]}-${parts[1]}`;
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 3 && `${itemParts[0]}-${itemParts[1]}` === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 4) {
          // Subtask level: collapse sibling subtasks
          const parentId = `${parts[0]}-${parts[1]}-${parts[2]}`;
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 4 && `${itemParts[0]}-${itemParts[1]}-${itemParts[2]}` === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        }
      }
      
      return newSet;
    });
  };

  /**
   * Checks if an item is currently expanded
   * @param id - The hierarchical ID of the item to check
   * @returns true if the item is expanded, false otherwise
   */
  const isExpanded = (id: string) => expandedItems.has(id);

  /**
   * Sets the delete target to show the confirmation modal
   * @param target - The DeleteTarget object containing information about what to delete
   */
  const handleDelete = (target: DeleteTarget) => {
    // Check restrictions before allowing delete
    if (target.type === "phase") {
      const phase = phases[target.phaseIndex];
      if (isPhaseRestricted(phase.id)) {
        return; // Restricted - cannot delete
      }
    } else if (target.type === "part" && target.partIndex !== undefined) {
      const phase = phases[target.phaseIndex];
      const part = phase.parts[target.partIndex];
      if (isPartRestricted(phase.id, part.id)) {
        return; // Restricted - cannot delete
      }
    } else if (target.type === "task" && target.partIndex !== undefined && target.taskIndex !== undefined) {
      const phase = phases[target.phaseIndex];
      const part = phase.parts[target.partIndex];
      const task = part.tasks[target.taskIndex];
      if (isTaskRestricted(phase.id, part.id, task.id)) {
        return; // Restricted - cannot delete
      }
    } else if (target.type === "subtask" && target.partIndex !== undefined && target.taskIndex !== undefined && target.subtaskIndex !== undefined) {
      const phase = phases[target.phaseIndex];
      const part = phase.parts[target.partIndex];
      const task = part.tasks[target.taskIndex];
      const subtask = task.subtasks[target.subtaskIndex];
      if (isSubtaskRestricted(phase.id, part.id, task.id, subtask.subtask_id)) {
        return; // Restricted - cannot delete
      }
    }
    setDeleteTarget(target);
  };

  /**
   * Confirms and executes the deletion of a phase, part, task, or subtask
   * Step 1: Validate deleteTarget exists
   * Step 2: Extract indices from deleteTarget
   * Step 3: Remove the item based on type (phase/part/task/subtask)
   * Step 4: Regenerate IDs for remaining items at the same level to maintain sequential order
   * Step 5: Update phases state
   * Step 6: Clear deleteTarget
   */
  const confirmDelete = () => {
    // Step 1: Validation
    if (!deleteTarget) return;

    // Step 2: Extract indices
    const { type, phaseIndex, partIndex, taskIndex, subtaskIndex } = deleteTarget;
    const updatedPhases = [...phases];

    // Step 3 & 4: Delete and regenerate IDs based on type
    if (type === "phase") {
      updatedPhases.splice(phaseIndex, 1);
      updatedPhases.forEach((phase, idx) => {
        phase.id = generateId("phase", idx);
      });
    } else if (type === "part" && partIndex !== undefined) {
      updatedPhases[phaseIndex].parts.splice(partIndex, 1);
      updatedPhases[phaseIndex].parts.forEach((part, idx) => {
        part.id = generateId("part", idx);
      });
    } else if (type === "task" && partIndex !== undefined && taskIndex !== undefined) {
      updatedPhases[phaseIndex].parts[partIndex].tasks.splice(taskIndex, 1);
      updatedPhases[phaseIndex].parts[partIndex].tasks.forEach((task, idx) => {
        task.id = generateId("task", idx);
      });
    } else if (type === "subtask" && partIndex !== undefined && taskIndex !== undefined && subtaskIndex !== undefined) {
      updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks.splice(subtaskIndex, 1);
      updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks.forEach((subtask, idx) => {
        subtask.subtask_id = generateId("subtask", idx);
      });
    }

    // Step 5 & 6: Update state and clear delete target
    setPhases(updatedPhases);
    setDeleteTarget(null);
  };

  /**
   * Handles drag end event for phases reordering
   * Step 1: Check if drag destination exists
   * Step 2: Check if source or destination is restricted
   * Step 3: Reorder phases array based on drag result
   * Step 4: Regenerate IDs for all phases to maintain sequential order
   * Step 5: Update phases state
   */
  const handlePhaseDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourcePhase = phases[result.source.index];
    const destPhase = phases[result.destination.index];
    
    // Check restrictions
    if (isPhaseRestricted(sourcePhase.id) || isPhaseRestricted(destPhase.id)) {
      return; // Restricted - cannot drag
    }

    const items = Array.from(phases);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    items.forEach((phase, idx) => {
      phase.id = generateId("phase", idx);
    });

    setPhases(items);
  };

  /**
   * Handles drag end event for parts reordering within a phase
   * Step 1: Check if drag destination exists
   * Step 2: Check if source or destination is restricted
   * Step 3: Reorder parts array based on drag result
   * Step 4: Regenerate IDs for all parts to maintain sequential order
   * Step 5: Update phases state
   */
  const handlePartDragEnd = (result: DropResult, phaseIndex: number) => {
    if (!result.destination) return;

    const phase = phases[phaseIndex];
    const sourcePart = phase.parts[result.source.index];
    const destPart = phase.parts[result.destination.index];
    
    // Check restrictions
    if (isPartRestricted(phase.id, sourcePart.id) || isPartRestricted(phase.id, destPart.id)) {
      return; // Restricted - cannot drag
    }

    const items = Array.from(phases[phaseIndex].parts);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    items.forEach((part, idx) => {
      part.id = generateId("part", idx);
    });

    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts = items;
    setPhases(updatedPhases);
  };

  /**
   * Handles drag end event for tasks reordering within a part
   * Step 1: Check if drag destination exists
   * Step 2: Check if source or destination is restricted
   * Step 3: Reorder tasks array based on drag result
   * Step 4: Regenerate IDs for all tasks to maintain sequential order
   * Step 5: Update phases state
   */
  const handleTaskDragEnd = (result: DropResult, phaseIndex: number, partIndex: number) => {
    if (!result.destination) return;

    const phase = phases[phaseIndex];
    const part = phase.parts[partIndex];
    const sourceTask = part.tasks[result.source.index];
    const destTask = part.tasks[result.destination.index];
    
    // Check restrictions
    if (isTaskRestricted(phase.id, part.id, sourceTask.id) || isTaskRestricted(phase.id, part.id, destTask.id)) {
      return; // Restricted - cannot drag
    }

    const items = Array.from(phases[phaseIndex].parts[partIndex].tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    items.forEach((task, idx) => {
      task.id = generateId("task", idx);
    });

    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks = items;
    setPhases(updatedPhases);
  };

  /**
   * Handles drag end event for subtasks reordering within a task
   * Step 1: Check if drag destination exists
   * Step 2: Check if source or destination is restricted
   * Step 3: Reorder subtasks array based on drag result
   * Step 4: Regenerate IDs for all subtasks to maintain sequential order
   * Step 5: Update phases state
   */
  const handleSubtaskDragEnd = (result: DropResult, phaseIndex: number, partIndex: number, taskIndex: number) => {
    if (!result.destination) return;

    const phase = phases[phaseIndex];
    const part = phase.parts[partIndex];
    const task = part.tasks[taskIndex];
    const sourceSubtask = task.subtasks[result.source.index];
    const destSubtask = task.subtasks[result.destination.index];
    
    // Check restrictions
    if (isSubtaskRestricted(phase.id, part.id, task.id, sourceSubtask.subtask_id) || 
        isSubtaskRestricted(phase.id, part.id, task.id, destSubtask.subtask_id)) {
      return; // Restricted - cannot drag
    }

    const items = Array.from(phases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    items.forEach((subtask, idx) => {
      subtask.subtask_id = generateId("subtask", idx);
    });

    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks = items;
    setPhases(updatedPhases);
  };

  /**
   * Sets the action type (select/assign) for a subtask and shows dropdowns
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param actionType - The action type: "select" or "assign"
   */
  const handleSelectOrAssign = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, actionType: "select" | "assign") => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].showDropdowns = true;
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].actionType = actionType;
    setPhases(updatedPhases);
  };

  /**
   * Updates the selected content level for a subtask.
   * For MCQ/coding select mode: commits current selectedContentId to the current level array before switching,
   * then loads the new level's IDs into selectedContentId so changing the dropdown does not move questions.
   */
  const handleContentLevelChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, level: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    const isMcqCodingSelect = (subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING) && subtask.actionType === "select";
    const oldLevel = subtask.selectedContentLevel;

    if (isMcqCodingSelect && oldLevel && (oldLevel === "level1" || oldLevel === "level2" || oldLevel === "level3")) {
      const selectedIds = (subtask.selectedContentId || "").split(",").filter((id: string) => id.trim());
      if (selectedIds.length > 0) {
        subtask[oldLevel] = selectedIds;
      }
    }

    subtask.selectedContentLevel = level;

    if (isMcqCodingSelect && level) {
      const newLevelIds = Array.isArray(subtask[level]) ? subtask[level] : [];
      subtask.selectedContentId = newLevelIds.map((id: any) => String(id)).join(",");
    }

    setPhases(updatedPhases);
  };

  /**
   * Fetches available content for a subtopic and updates the subtask
   * Step 1: Make API call to get content for the subtopic
   * Step 2: Update the subtask's availableContent with the response data
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param subtopicId - The subtopic ID to fetch content for
   */
  const handleFetchContent = async (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, subtopicId: string) => {
    const subtask = phases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    
    // Only trigger API in select mode for video or notes
    const isSelectMode = subtask.actionType === "select";
    const isVideoOrNotes = subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES;
    
    if (!isSelectMode || !isVideoOrNotes) {
      return;
    }
    
    try {
      const response = await authService.post('admin/content/get_content_for_subtopic/', {
        sub_topic_id: subtopicId
      });
      
      const updatedPhases = [...phases];
      updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].availableContent = response.data;
      setPhases(updatedPhases);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  /**
   * Updates the selected content ID, level, and time for a subtask
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param contentId - The selected content ID
   * @param level - The content level
   * @param time - The content time (for videos)
   */
  const handleContentSelected = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, contentId: string, level: string, time: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    subtask.selectedContentLevel = level;
    subtask.selectedContentTime = time;
    
    // For VIDEO/NOTES/PROJECT_CODING: Store content ID in subtask.id
    if ((subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES || subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING) && contentId) {
      subtask.id = contentId;
    } else {
      // For MCQ/CODING: Store in selectedContentId (comma-separated for multiple selection)
      subtask.selectedContentId = contentId;
    }
    
    setPhases(updatedPhases);
  };

  /**
   * Updates the level arrays (level1, level2, level3) for a subtask after content creation
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param level1 - Array of content IDs for level 1
   * @param level2 - Array of content IDs for level 2
   * @param level3 - Array of content IDs for level 3
   */
  const handleContentCreated = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, level1: string[], level2: string[], level3: string[]) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.level1 = level1;
    subtask.level2 = level2;
    subtask.level3 = level3;
    setPhases(updatedPhases);
  };

  /**
   * Opens the preview modal for video or notes content
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param type - The content type: "video" or "notes"
   * @param content - The content object to preview
   */
  const handlePreviewContent = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, type: typeof SUBTASK_TYPE.VIDEO | typeof SUBTASK_TYPE.NOTES, content: any) => {
    const subtask = phases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    setPreviewContent({ type, content, subtask });
    setShowPreviewModal(true);
  };

  /**
   * Builds the VdoCipher video player URL from OTP and playback info
   * @param otp - The OTP token for video authentication
   * @param playback_info - The playback info token
   * @returns The complete video player URL
   */
  const buildVideoUrl = (otp: string, playback_info: string): string => {
    return `https://player.vdocipher.com/v2/?otp=${otp}&playbackInfo=${playback_info}`;
  };

  /**
   * Updates the content count for a specific level in a subtask
   * Step 1: Initialize contentCounts object if it doesn't exist
   * Step 2: Update the count for the specified level
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param level - The level key ("level1", "level2", or "level3")
   * @param count - The count value as a string
   */
  const handleContentCountChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, level: string, count: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    if (!subtask.contentCounts) {
      subtask.contentCounts = { level1: "0", level2: "0", level3: "0" };
    }
    subtask.contentCounts[level as keyof typeof subtask.contentCounts] = count;
    setPhases(updatedPhases);
  };

  /**
   * Creates blank content for a subtask (for video/notes assign mode)
   * Step 1: Validate required subtask properties
   * Step 2: Build payload with task ID and content count
   * Step 3: Make API call to create blank content
   * Step 4: Extract created content IDs from response
   * Step 5: Update subtask's createdContent array
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   */
  const handleAddContent = async (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number) => {
    const subtask = phases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    if (!subtask.subtopicId || !subtask.selectedContentLevel || !subtask.subtaskType) return;

    const task = phases[phaseIndex].parts[partIndex].tasks[taskIndex];
    const level = subtask.selectedContentLevel;
    const countObj: any = { level1: "", level2: "", level3: "" };
    countObj[level] = "1";

    const payload: any = {
      [task.id]: {
        subtopic_id: subtask.subtopicId,
        content: [
          {
            type: subtask.subtaskType,
            count: countObj,
          }
        ]
      },
      assigned_to: subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING ? ((subtask as any).cc_id || '') : ''
    };

    try {
      setLoading(true);
      const response = await authService.post(`admin/content/create_blank_content/${selectedProject}/`, payload);
      
      const updatedPhases = [...phases];
      const updatedSubtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
      
      if (!updatedSubtask.createdContent) {
        updatedSubtask.createdContent = [];
      }
      
      const taskResponse = response.data?.[task.id];
      if (taskResponse && taskResponse.content && taskResponse.content.length > 0) {
        const contentData = taskResponse.content[0];
        const contentItem: ContentItem = {
          type: subtask.subtaskType as SubtaskTypes,
          count: {
            level1: contentData.count?.level1 || [],
            level2: contentData.count?.level2 || [],
            level3: contentData.count?.level3 || []
          }
        };
        
        updatedSubtask.createdContent = [contentItem];
        
        // For PROJECT_CODING/VIDEO/NOTES: Set subtask.id with the question/content ID from createdContent
        if (subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING || subtask.subtaskType === SUBTASK_TYPE.VIDEO || subtask.subtaskType === SUBTASK_TYPE.NOTES) {
          const assignedIds = level === "level1" ? contentData.count?.level1 : level === "level2" ? contentData.count?.level2 : contentData.count?.level3;
          if (assignedIds && assignedIds.length > 0) {
            updatedSubtask.id = String(assignedIds[0]);
          }
        }
      }
      
      
      setPhases(updatedPhases);
      setLoading(false);
    } catch (error) {
      console.error('Error creating content:', error);
      setLoading(false);
    }
  };

  /**
   * Updates the content creator ID for a subtask (for PROJECT_CODING in assign mode)
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param ccId - The content creator user ID
   */
  const handleCcChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, ccId: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    (subtask as any).cc_id = ccId;
    setPhases(updatedPhases);
  };

  /**
   * Clears all content selection on a subtask (MCQ, coding, video, notes, project_coding).
   * Call when subject, topic, or subtopic changes so old selection does not persist.
   */
  const clearSubtaskContentSelection = (subtask: any) => {
    subtask.level1 = [];
    subtask.level2 = [];
    subtask.level3 = [];
    subtask.selectedContentId = "";
    subtask.selectedContentLevel = "";
    subtask.selectedContentTime = "";
    subtask.level = "";
    if (subtask.hasOwnProperty("id")) subtask.id = undefined;
    if (subtask.contentCounts) {
      subtask.contentCounts = { level1: "0", level2: "0", level3: "0" };
    }
  };

  /**
   * Updates the subject ID for a subtask and clears dependent fields
   * Step 1: Update subjectId
   * Step 2: Clear topicId and subtopicId (cascade clear)
   * Step 3: Clear content selection (level1/2/3, selectedContentId, id, etc.)
   * Step 4: Fetch topics for the new subject
   */
  const handleSubjectChange = async (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, subjectId: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.subjectId = subjectId;
    subtask.topicId = "";
    subtask.subtopicId = "";
    clearSubtaskContentSelection(subtask);
    setPhases(updatedPhases);
    await fetchTopics(subjectId);
  };

  /**
   * Updates the topic ID for a subtask, clears subtopicId, and clears content selection
   */
  const handleTopicChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, topicId: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.topicId = topicId;
    subtask.subtopicId = "";
    clearSubtaskContentSelection(subtask);
    setPhases(updatedPhases);
  };

  /**
   * Updates the subtopic ID for a subtask and clears content selection
   */
  const handleSubtopicChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, subtopicId: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.subtopicId = subtopicId;
    clearSubtaskContentSelection(subtask);
    setPhases(updatedPhases);
  };

  /**
   * Updates the subtask type and clears level if not applicable
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param subtaskType - The new subtask type
   */
  const handleSubtaskTypeChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, subtaskType: SubtaskTypes) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.subtaskType = subtaskType;
    clearSubtaskContentSelection(subtask);
    if (!SUBTASK_TYPES_WITH_LEVEL.includes(subtaskType)) {
      subtask.level = "";
    }
    if (subtaskType !== SUBTASK_TYPE.CODING) {
      subtask.editor = null;
    }
    setPhases(updatedPhases);
  };

  /**
   * Updates the editor property for a subtask
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param editor - The editor type
   */
  const handleEditorChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, editor: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].editor = editor || null;
    setPhases(updatedPhases);
  };

  /**
   * Updates the level property for a subtask
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param level - The new level value
   */
  const handleLevelChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, level: string) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].level = level;
    setPhases(updatedPhases);
  };

  /**
   * Updates the mandatory flag for a subtask
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param checked - The new mandatory state (true/false)
   */
  const handleMandatoryChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, checked: boolean) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex].isMandatory = checked;
    setPhases(updatedPhases);
  };

  /**
   * Updates the description for a project_coding subtask
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param description - The description text
   */
  const handleDescriptionChange = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, description: string) => {
    const updatedPhases = [...phases];
    const subtask = updatedPhases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex] as any;
    subtask.description = description;
    setPhases(updatedPhases);
  };


  /**
   * Builds the save data payload from current phases state
   * @returns The save data object ready to be sent to the API
   */
  const buildSaveData = () => {
    const userEmail = sessionStorage.getItem("Email") || "";
    
    return {
      project_id: selectedProject,
      project_data: {
        content: phases.map(phase => ({
          phase_id: phase.id,
          phase_name: phase.name,
          parts: phase.parts.map(part => ({
            part_id: part.id,
            part_name: part.name,
            tasks: part.tasks.map(task => ({
              task_id: task.id,
              task_name: task.name,
              data: task.subtasks.map(subtask => {
                const subtaskData: any = {
                  subtask_name: subtask.name,
                  type: subtask.subtaskType,
                  topic_id: subtask.topicId,
                  subject_id: subtask.subjectId,
                  subtask_id: subtask.subtask_id, // Subtask identifier
                  is_mandatory: subtask.isMandatory,
                  sub_topic_id: subtask.subtopicId,
                  mode: subtask.actionType || ""
                };

                // Add editor for coding type
                if (subtask.subtaskType === SUBTASK_TYPE.CODING && subtask.editor) {
                  subtaskData.editor = subtask.editor;
                }

                if (subtask.actionType === "select") {
                  const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
                  const subtaskAny = subtask as any;

                  if (isMcqCoding && subtask.selectedContentLevel) {
                    // Only update the level that matches the dropdown; preserve other levels so changing dropdown does not move questions
                    const selectedIds = (subtask.selectedContentId || "").split(",").filter((id: string) => id.trim());
                    subtaskData.level1 = subtask.selectedContentLevel === "level1" ? selectedIds : (Array.isArray(subtaskAny.level1) ? [...subtaskAny.level1] : []);
                    subtaskData.level2 = subtask.selectedContentLevel === "level2" ? selectedIds : (Array.isArray(subtaskAny.level2) ? [...subtaskAny.level2] : []);
                    subtaskData.level3 = subtask.selectedContentLevel === "level3" ? selectedIds : (Array.isArray(subtaskAny.level3) ? [...subtaskAny.level3] : []);
                  } else if (!isMcqCoding && subtask.id && subtask.selectedContentLevel) {
                    // For VIDEO, NOTES, and PROJECT_CODING: use subtask.id directly
                    subtaskData.id = subtask.id;
                    subtaskData.time = subtask.selectedContentTime || "";
                    subtaskData.level = subtask.selectedContentLevel;
                  }
                } else if (subtask.actionType === "assign") {
                  const isMcqCoding = subtask.subtaskType === SUBTASK_TYPE.MCQ || subtask.subtaskType === SUBTASK_TYPE.CODING;
                  
                  if (isMcqCoding) {
                    // For mcq/coding assign mode, save level arrays
                    const subtaskAny = subtask as any;
                    subtaskData.level1 = Array.isArray(subtaskAny.level1) ? subtaskAny.level1 : [];
                    subtaskData.level2 = Array.isArray(subtaskAny.level2) ? subtaskAny.level2 : [];
                    subtaskData.level3 = Array.isArray(subtaskAny.level3) ? subtaskAny.level3 : [];
                  } else if (subtask.id && subtask.selectedContentLevel) {
                    // For video/notes/project_coding assign mode, use subtask.id directly
                    subtaskData.id = subtask.id;
                    subtaskData.level = subtask.selectedContentLevel;
                    
                    // Add user_id for PROJECT_CODING in assign mode
                    if (subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING && subtask.actionType === "assign") {
                      const subtaskAny = subtask as any;
                      if (subtaskAny.cc_id) {
                        subtaskData.user_id = subtaskAny.cc_id;
                      }
                    }
                  }
                }
                
                // Add description for project_coding (always add for project_coding type)
                if (subtask.subtaskType === SUBTASK_TYPE.PROJECT_CODING) {
                  const subtaskAny = subtask as any;
                  // Always save description for project_coding (default to empty string if not set)
                  subtaskData.description = (subtaskAny.description !== undefined && subtaskAny.description !== null) 
                    ? String(subtaskAny.description) 
                    : "";
                }

                return subtaskData;
              })
            }))
          }))
        }))
      },
      by: userEmail
    };
  };

  /**
   * Auto-saves the project configuration to the server
   * This function is called automatically when phases change (debounced)
   */
  const autoSave = async () => {
    if (!selectedProject || phases.length === 0) {
      return;
    }

    // Check if project status is Published - show modal instead of saving
    if (projectStatus === "Published") {
      setPendingAutoSave(true);
      setShowPublishedEditModal(true);
      return;
    }

    try {
      setIsSaving(true);
      const saveData = buildSaveData();
      const response = await authService.post('admin/check_content_mapping_for_project/', saveData);
      
      // Update content status with response data (same structure as fetchContentStatus)
      if (response.data) {
        setContentStatus({
          [SUBTASK_TYPE.VIDEO]: response.data[SUBTASK_TYPE.VIDEO] || {},
          [SUBTASK_TYPE.NOTES]: response.data[SUBTASK_TYPE.NOTES] || {},
          [SUBTASK_TYPE.MCQ]: response.data[SUBTASK_TYPE.MCQ] || {},
          [SUBTASK_TYPE.CODING]: response.data[SUBTASK_TYPE.CODING] || {},
          [SUBTASK_TYPE.PROJECT_CODING]: response.data[SUBTASK_TYPE.PROJECT_CODING] || {}
        });
        // Store full content status data for header
        setContentStatusData(response.data);
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error auto-saving configuration:', error);
      setIsSaving(false);
    }
  };

  /**
   * Handles Yes action for Published project edit modal
   * Step 1: Update project state to Configured
   * Step 2: Update batch_project_changed_tag
   * Step 3: Perform autosave
   */
  const handlePublishedEditConfirm = async () => {
    setShowPublishedEditModal(false);
    setIsSaving(true);
    try {
      // Step 1: Update project state to Configured
      await authService.put('admin/project_state/update/', {
        project_id: parseInt(selectedProject),
        new_state: "Configured"
      });

      // Step 2: Update batch_project_changed_tag
      await authService.put('admin/batch_project_changed_tag/update/', {
        project_id: parseInt(selectedProject)
      });

      // Refresh projects list in header to get updated status
      if (refreshProjectsRef.current) {
        await refreshProjectsRef.current();
      }

      // Step 3: Update local projectStatus and perform autosave
      setProjectStatus("Configured");
      setPendingAutoSave(false);
      
      const saveData = buildSaveData();
      const response = await authService.post('admin/check_content_mapping_for_project/', saveData);
      
      // Update content status with response data
      if (response.data) {
        setContentStatus({
          [SUBTASK_TYPE.VIDEO]: response.data[SUBTASK_TYPE.VIDEO] || {},
          [SUBTASK_TYPE.NOTES]: response.data[SUBTASK_TYPE.NOTES] || {},
          [SUBTASK_TYPE.MCQ]: response.data[SUBTASK_TYPE.MCQ] || {},
          [SUBTASK_TYPE.CODING]: response.data[SUBTASK_TYPE.CODING] || {},
          [SUBTASK_TYPE.PROJECT_CODING]: response.data[SUBTASK_TYPE.PROJECT_CODING] || {}
        });
        setContentStatusData(response.data);
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error updating project state and saving:', error);
      setIsSaving(false);
    }
  };

  /**
   * Handles No action for Published project edit modal
   * Navigates back to project listing screen
   */
  const handlePublishedEditCancel = () => {
    setShowPublishedEditModal(false);
    setPendingAutoSave(false);
    navigate('/admin/project', {
      state: {
        track_id: selectedTrack
      }
    });
  };

 
  /**
   * Handles track selection change and resets related state
   * Step 1: Set the selected track
   * Step 2: Clear selected project, project name, allowed subjects, and phases
   */
  const handleTrackChange = (trackId: string) => {
    // Step 1: Set track
    setSelectedTrack(trackId);
    // Step 2: Clear project-related state
    setSelectedProject("");
    setSelectedProjectName("");
    setProjectAllowedSubjects([]);
    setPhases([]);
  };
  
  return (
    <div className="border rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, display: "flex", flexDirection: "column" }}>
      <ProjectConfigurationHeader
        selectedTrack={selectedTrack}
        selectedProject={selectedProject}
        selectedProjectName={selectedProjectName}
        loading={loading || isSaving}
        onTrackChange={handleTrackChange}
        onProjectChange={handleProjectChange}
        onSubjectsChange={handleSubjectsChange}
        onAddPhase={addPhase}
        contentStatusData={contentStatusData}
        onProjectStatusChange={setProjectStatus}
        refreshProjectsRef={refreshProjectsRef}
        onLastUpdatableTagChange={setLastUpdatableTag}
      />
      <div className="p-3" style={{ overflowY: "auto", flex: 1 }}>

        <DragDropContext onDragEnd={handlePhaseDragEnd}>
          <Droppable droppableId="phases">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {phases.map((phase, phaseIndex) => {
                  const phaseId = phase.id;
                  return (
                    <Draggable 
                      key={phaseId} 
                      draggableId={phaseId} 
                      index={phaseIndex}
                      isDragDisabled={isPhaseRestricted(phaseId)}
                    >
                      {(provided, snapshot) => {
                        const phaseRestricted = isPhaseRestricted(phaseId);
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'transparent',
                              opacity: phaseRestricted ? 0.6 : 1
                            }}
                          >
                          <PhaseComponent
                            phase={phase}
                            phaseIndex={phaseIndex}
                            phaseId={phaseId}
                            isExpanded={isExpanded(phaseId)}
                            projectId={selectedProject}
                            subjects={filteredSubjects}
                            topicsCache={topicsCache}
                            contentStatus={contentStatus}
                            dragHandleProps={provided.dragHandleProps}
                            onToggleExpand={() => toggleExpand(phaseId)}
                            onNameChange={(name) => updatePhaseName(phaseIndex, name)}
                            allPhases={phases}
                            onAddPart={() => addPart(phaseIndex)}
                            onDelete={handleDelete}
                            onPartDragEnd={(result) => handlePartDragEnd(result, phaseIndex)}
                            onPartToggleExpand={(partId) => toggleExpand(partId)}
                            onPartNameChange={(partIndex, name) => updatePartName(phaseIndex, partIndex, name)}
                            onAddTask={(partIndex) => addTask(phaseIndex, partIndex)}
                            onPartDelete={handleDelete}
                            onTaskDragEnd={(partIndex, result) => handleTaskDragEnd(result, phaseIndex, partIndex)}
                            onTaskToggleExpand={(taskId) => toggleExpand(taskId)}
                            onTaskNameChange={(partIndex, taskIndex, name) => updateTaskName(phaseIndex, partIndex, taskIndex, name)}
                            onAddSubtask={(partIndex, taskIndex) => addSubtask(phaseIndex, partIndex, taskIndex)}
                            onTaskDelete={handleDelete}
                            onSubtaskDragEnd={(partIndex, taskIndex, result) => handleSubtaskDragEnd(result, phaseIndex, partIndex, taskIndex)}
                            onSelectOrAssign={(partIndex, taskIndex, subtaskIndex, actionType) => handleSelectOrAssign(phaseIndex, partIndex, taskIndex, subtaskIndex, actionType)}
                            onSubtaskNameChange={(partIndex, taskIndex, subtaskIndex, name) => updateSubtaskName(phaseIndex, partIndex, taskIndex, subtaskIndex, name)}
                            onMandatoryChange={(partIndex, taskIndex, subtaskIndex, checked) => handleMandatoryChange(phaseIndex, partIndex, taskIndex, subtaskIndex, checked)}
                            onSubjectChange={(partIndex, taskIndex, subtaskIndex, subjectId) => handleSubjectChange(phaseIndex, partIndex, taskIndex, subtaskIndex, subjectId)}
                            onTopicChange={(partIndex, taskIndex, subtaskIndex, topicId) => handleTopicChange(phaseIndex, partIndex, taskIndex, subtaskIndex, topicId)}
                            onSubtopicChange={(partIndex, taskIndex, subtaskIndex, subtopicId) => handleSubtopicChange(phaseIndex, partIndex, taskIndex, subtaskIndex, subtopicId)}
                            onSubtaskTypeChange={(partIndex, taskIndex, subtaskIndex, subtaskType) => handleSubtaskTypeChange(phaseIndex, partIndex, taskIndex, subtaskIndex, subtaskType as SubtaskTypes)}
                            onEditorChange={(partIndex, taskIndex, subtaskIndex, editor) => handleEditorChange(phaseIndex, partIndex, taskIndex, subtaskIndex, editor)}
                            onLevelChange={(partIndex, taskIndex, subtaskIndex, level) => handleLevelChange(phaseIndex, partIndex, taskIndex, subtaskIndex, level)}
                            onContentLevelChange={(partIndex, taskIndex, subtaskIndex, level) => handleContentLevelChange(phaseIndex, partIndex, taskIndex, subtaskIndex, level)}
                            onContentCountChange={(partIndex, taskIndex, subtaskIndex, level, count) => handleContentCountChange(phaseIndex, partIndex, taskIndex, subtaskIndex, level, count)}
                            onAddContent={(partIndex, taskIndex, subtaskIndex) => handleAddContent(phaseIndex, partIndex, taskIndex, subtaskIndex)}
                            onFetchContent={(partIndex, taskIndex, subtaskIndex, subtopicId) => handleFetchContent(phaseIndex, partIndex, taskIndex, subtaskIndex, subtopicId)}
                            onContentSelected={(partIndex, taskIndex, subtaskIndex, contentId, level, time) => handleContentSelected(phaseIndex, partIndex, taskIndex, subtaskIndex, contentId, level, time)}
                            onPreviewContent={(partIndex, taskIndex, subtaskIndex, type, content) => handlePreviewContent(phaseIndex, partIndex, taskIndex, subtaskIndex, type, content)}
                            onContentCreated={(partIndex, taskIndex, subtaskIndex, level1, level2, level3) => handleContentCreated(phaseIndex, partIndex, taskIndex, subtaskIndex, level1, level2, level3)}
                            onSubtaskDelete={handleDelete}
                            onFetchTopics={fetchTopics}
                            onDescriptionChange={(partIndex, taskIndex, subtaskIndex, description) => handleDescriptionChange(phaseIndex, partIndex, taskIndex, subtaskIndex, description)}
                            onCcChange={(partIndex, taskIndex, subtaskIndex, ccId) => handleCcChange(phaseIndex, partIndex, taskIndex, subtaskIndex, ccId)}
                            assignedCcIds={assignedCcIds}
                            generateId={generateId}
                            isPartExpanded={(partId) => isExpanded(partId)}
                            isTaskExpanded={(taskId) => isExpanded(taskId)}
                            isSubtaskExpanded={(subtaskId) => isExpanded(subtaskId)}
                            toggleSubtaskExpand={(subtaskId) => toggleExpand(subtaskId)}
                            isPhaseRestricted={isPhaseRestricted}
                            isPartRestricted={isPartRestricted}
                            isTaskRestricted={isTaskRestricted}
                            isSubtaskRestricted={isSubtaskRestricted}
                          />
        </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
          </div>
        )}
          </Droppable>
        </DragDropContext>
      </div>

      <PreviewModal
        show={deleteTarget !== null}
        onHide={() => setDeleteTarget(null)}
        title="Confirm Delete"
        content={
          deleteTarget ? (
            <div>
              Are you sure you want to delete this {deleteTarget.type}: <strong>{deleteTarget.name || "Untitled"}</strong>?
              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                  No
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                  Yes
                </Button>
              </div>
            </div>
          ) : null
        }
        error={null}
        centered={true}
        showFooter={false}
      />
      {showPreviewModal && previewContent && (
        <PreviewModal
          show={showPreviewModal}
          onHide={() => {
            setShowPreviewModal(false);
            setPreviewContent(null);
          }}
          title="Preview"
          content={
            previewContent.type === SUBTASK_TYPE.VIDEO && previewContent.content.otp && previewContent.content.playback_info && 
            previewContent.content.otp.trim() !== "" && previewContent.content.playback_info.trim() !== "" ? (
              <iframe
                src={buildVideoUrl(previewContent.content.otp, previewContent.content.playback_info)}
                width="100%"
                height="500px"
                title="Video Preview"
                allowFullScreen
                allow="encrypted-media; autoplay; clipboard-write"
                style={{ border: "none" }}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            ) : previewContent.type === SUBTASK_TYPE.NOTES && previewContent.content.content && previewContent.content.content.trim() !== "" ? (
              <div className="p-0 m-0 ps-3" dangerouslySetInnerHTML={{ __html: previewContent.content.content }} />
            ) : null
          }
          error={
            previewContent.type === SUBTASK_TYPE.VIDEO && 
            (!previewContent.content.otp || previewContent.content.otp.trim() === "" || 
             !previewContent.content.playback_info || previewContent.content.playback_info.trim() === "") ? 
            "Content not updated" :
            previewContent.type === SUBTASK_TYPE.NOTES && 
            (!previewContent.content.content || previewContent.content.content.trim() === "") ?
            "Content not updated" : null
          }
        />
      )}

      {/* Published Project Edit Modal */}
      <PreviewModal
        show={showPublishedEditModal}
        onHide={handlePublishedEditCancel}
        title="Edit Published Project"
        content={
          <div>
            <p>If you want to edit this Project you will have remove status from Published to Configured</p>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="secondary" onClick={handlePublishedEditCancel}>
                No
              </Button>
              <Button variant="primary" onClick={handlePublishedEditConfirm} disabled={isSaving}>
                {isSaving ? "Processing..." : "Yes"}
              </Button>
            </div>
          </div>
        }
        error={null}
        centered={true}
        showFooter={false}
      />

    </div>
  );
};

export default ProjectConfiguration;
